import { makeStyles } from '@mui/styles';
import { MediaType } from '@nice-ccf/core-sdk';
import {
  getRtigSuggestionCardName,
  globalActions,
  activeContactSelector,
} from '@nice-ccf/ui-components';
import {
  CcfActiveListeningIcon,
  CcfAskQuestionIcon,
  CcfBeEmpatheticIcon,
  CcfBox,
  CcfBuildRapportIcon,
  CcfCard,
  CcfCloseIcon,
  CcfDemonstrateOwnerShipIcon,
  CcfEmpowerClientIcon,
  CcfIdentifySolutionIcon,
  CcfNoInterruptionIcon,
  CcfSetExpectationIcon,
  CcfSpeechVelocityIcon,
  CcfThanksForLoyalityIcon,
  CcfTypography,
} from '@nice-ccf/ui-controls';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ccfrtigActions,
  selectIsSuggestionSummaryExpanded,
  selectsuggestionsList,
} from '../+state/ccf-rtig.slice';
import styles from './notification-message.module.scss';

/* eslint-disable-next-line */
export interface NotificationMessageProps {
  suggestionItem: any;
  suggestionRemoveCallback: any;
  id: number;
  highLightColor: boolean;
}

/**
 * Component to render Notification Message in suggestion summary
 * @param param0 - NotificationMessageProps
 * @example - <NotificationMessage />
 * @returns
 */
export function NotificationMessage(props: any) {
  const [timerLabel, setTimerLabel] = useState('now');
  const isSuggestionSummaryExpanded = useSelector(
    selectIsSuggestionSummaryExpanded
  );
  const dispatch = useDispatch();
  const { setRtigSuggestionCardDetails } = globalActions;
  const { updateSuggestionList } = ccfrtigActions;
  const suggestionCardHeader = useSelector(getRtigSuggestionCardName);
  const suggestionsListObj = useSelector(selectsuggestionsList);
  const activeContact = useSelector(activeContactSelector);

  /**
   * Component to provide protected routes for application
   * @param param0 - NotificationMessageProps
   * @example - <CcfProtectedRoute />
   * @returns
   */
  const getTimerLabel = (notificationTimer: any) => {
    let label = 'now';
    const msToSec = 1000;
    const msToMin = 60000;
    const msToHour = 3600000;
    const msToDay = 86400000;

    if (notificationTimer) {
      const currentDate = new Date();
      const difInTime = currentDate.getTime() - notificationTimer.getTime();

      if (difInTime < msToMin) {
        label = fixedTime(difInTime, msToSec) + 's ago';
        if (label === '0s ago') {
          label = 'now';
        }
      }

      if (difInTime > msToMin && difInTime < msToHour) {
        label = fixedTime(difInTime, msToMin) + 'm ago';
      }

      if (difInTime > msToHour && difInTime < msToDay) {
        label = fixedTime(difInTime, msToHour) + 'h ago';
      }

      if (difInTime > msToDay) {
        label = fixedTime(difInTime, msToDay) + 'd ago';
      }
    }
    return label;
  };

  /**
   * Function to provide fixed time
   * @param param - dividend : number, divider : number
   * @example - fixedTime()
   * @returns
   */
  const fixedTime = (dividend: number, divider: number) => {
    return (dividend / divider).toFixed();
  };

  useEffect(() => {
    setTimerLabel(getTimerLabel(props.suggestionItem.receptionDate));
  }, [isSuggestionSummaryExpanded, props.suggestionItem.receptionDate]);

  /**
   * Component to Remove the suggestion Item from parent component
   * @param param -
   * @example - suggestionRemoveHandler()
   * @returns
   */
  const suggestionRemoveHandler = (e: any) => {
    props.suggestionRemoveCallback(props.id);
    //e.stopPropagation();
  };

  /**
   * Function to be called on RTIG notification message card
   * @param param -
   * @example - onNotificationMessageCLick()
   * @returns
   */
  const onNotificationMessageCLick = (param: string) => {
    if (activeContact?.media === MediaType.DIGITAL && activeContact.channel === 'Chat') {
      const suggestionCardCopy = suggestionsListObj;
      const suggestionCardCopy2: any = [];
      if (suggestionCardHeader !== param) {
        suggestionCardCopy.map((suggestionCard: any) => {
          const writablesuggestionCard = Object.assign({}, suggestionCard);
          if (suggestionCard.label === param) {
            writablesuggestionCard.highLight = true;
          } else {
            writablesuggestionCard.highLight = false;
          }
          suggestionCardCopy2.push(writablesuggestionCard);
        });
      } else if (suggestionCardHeader === param) {
        suggestionCardCopy.map((suggestionCard: any) => {
          const writablesuggestionCard = Object.assign({}, suggestionCard);
          writablesuggestionCard.highLight = false;
          suggestionCardCopy2.push(writablesuggestionCard);
        });
        param = '';
      }
      dispatch(setRtigSuggestionCardDetails(param));
      console.log(suggestionCardCopy);
      dispatch(updateSuggestionList(suggestionCardCopy2));
    }
  };
  /**
   * function  to render Icon For indivdual notifications
   * @param param0 - param
   * @example renderIconForIndivdualNotification()
   * @returns
   */
  function renderIconForIndivdualNotification(param: string) {
    switch (param) {
      case 'Thank For Loyalty':
        return <CcfThanksForLoyalityIcon />;
      case 'Active Listening':
        return <CcfActiveListeningIcon />;
      case 'Be Empathetic':
        return <CcfBeEmpatheticIcon />;
      case 'Build Rapport':
        return <CcfBuildRapportIcon />;
      case 'Take Ownership':
        return <CcfDemonstrateOwnerShipIcon />;
      case 'Ask Questions':
        return <CcfAskQuestionIcon />;
      case 'Set Expectations':
        return <CcfSetExpectationIcon />;
      case 'No Interruptions':
        return <CcfNoInterruptionIcon />;
      case 'Identify Solution':
        return <CcfIdentifySolutionIcon />;
      case 'Mpower Client':
        return <CcfEmpowerClientIcon />;
      case 'Speech Velocity':
        return <CcfSpeechVelocityIcon />;
      default:
        return '';
    }
  }

  const defaultStyles = {
    width: '300px',
    marginBottom: '16px',
  };

  return (
    <CcfBox
      className={styles.suggestionSummaryCardItem}
      component={'li'}
      onClick={() => onNotificationMessageCLick(props.suggestionItem.label)}
    >
      <CcfCard
        style={{
          ...defaultStyles,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '60px',
            width: '300px',
          }}
          className={styles.cardDetails}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              background: props.highLightColor ? '#ecf3f8' : '#ffffff',
            }}
          >
            <div className={styles.iconAndSugeestionTypeBlock}>
              <div className={styles.suggestionDetails}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    height: '60px',
                    marginLeft: '10px',
                  }}
                >
                  {renderIconForIndivdualNotification(
                    props.suggestionItem.label
                  )}
                  <div
                    style={{
                      marginLeft: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <CcfTypography
                      className={styles.suggestionTypeLabel}
                      style={{ minHeight: 0 }}
                    >
                      {props.suggestionItem.label}
                    </CcfTypography>

                    <CcfTypography className={styles.suggestionTypeDescription}>
                      {props.suggestionItem.message}
                    </CcfTypography>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.cardRightSection}>
              <CcfTypography
                className={styles.timerLabel}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  width: '60%',
                }}
              >
                {timerLabel}
              </CcfTypography>
              <CcfCloseIcon
                style={{
                  marginLeft: '10px',
                  width: '14px',
                  height: '14px',
                }}
                onClick={(e: any) => suggestionRemoveHandler(e)}
              />
            </div>
          </div>
        </div>
      </CcfCard>
    </CcfBox>
  );
}

export default NotificationMessage;
